import { useCallback, useContext, useMemo } from "react";
import { CButton, CCol, CForm, CRow } from '@coreui/react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { ConnectedAccountContext } from "../context/ConnectedAccountContext";
import { CInvalidFeedback } from "../../../../../../migration/CInvalidFeedback";
import CFormGroup from "../../../../../../migration/CFormGroup";
import CLabel from "../../../../../../migration/CLabel";
import { CInput } from "../../../../../../migration/CInput";
import Waiting from "../../../../../../general/Loadings/Waiting";
import StepCardHeader from "../components/StepCardHeaders";
import { useDispatch, useSelector } from "react-redux";
import { setCustomExternalDataConnectedAccounts } from "../../../../../../../actions/subscriber";
import { useActiveCustomExternalData } from "../../../../../../../helpers/customHooks";
import { callExternalDataApi } from "../../../../../../../apiCaller";
import { API_CUSTOM_EXTERNAL_DATA_CONNECTION } from "../../../../../../../constants";
import SyncFormikValues from "../../../../goals/intent-analysis/manage-intent-rules/components/SyncFormikValues";
import FormikDirtyWatcher from "../../../../goals/intent-analysis/manage-intent-rules/components/FormikDirtyWatcher";

const CURRENT_STEP = 1;

const FormGroup = ({
    name,
    label,
    errors,
    values,
    touched,
    ...props
}) => {
    return (
        <CFormGroup className="mb-3">
            <CLabel htmlFor={name}>{label}</CLabel>
            <CInput
                id={name}
                invalid={touched[name] && !!errors[name]}
                value={values[name]}
                {...props}
            />
            <CInvalidFeedback>{touched[name] && errors[name]}</CInvalidFeedback>
        </CFormGroup>
    );
}

const NameAndDescription = () => {
    const {
        activeStep,
        setActiveStep,
        stepsData,
        setStepsData,
        initStepsData,
        stepSubmitSnapshot,
        setStepSubmitSnapshot,
        onFormDirty,
        setRefetching,
        canUpdate,
    } = useContext(ConnectedAccountContext);

    const customExternalData = useActiveCustomExternalData();

    const dispatch = useDispatch();
    const { accountId, timeZone } = useSelector((state) => state.subscriber.activeAccount);
    const connectedAccounts = useSelector((state) => state.subscriber.customExternalData ? state.subscriber.customExternalData.connectedAccounts : []);

    const defaultInitialValuesStep = useMemo(() => ({
        name: initStepsData.name || "",
        description: initStepsData.description || "",
    }), [initStepsData]);

    const stepsDataStep = stepsData[CURRENT_STEP];
    const stepSnapshot = stepSubmitSnapshot[CURRENT_STEP];

    const initialValues = useMemo(
        () => stepsDataStep || stepSnapshot || defaultInitialValuesStep,
        [stepsDataStep, stepSnapshot, defaultInitialValuesStep]
    );

    const onSubmit = useCallback(async (values, { setSubmitting }) => {
        const trimmedName = values.name.trim();
        const trimmedDescription = values.description?.trim() || '';

        const hasNoChanges =
            trimmedName === stepsData.name &&
            trimmedDescription === stepsData.description;

        if (hasNoChanges) {
            setActiveStep(CURRENT_STEP + 1);
            return;
        }

        setSubmitting(true);

        const payload = {
            name: trimmedName,
            description: trimmedDescription,
            accountId,
            timeZone,
            externalId: customExternalData.id,
            lastEditStep: stepsData.lastEditStep && stepsData.lastEditStep > CURRENT_STEP
                ? stepsData.lastEditStep
                : CURRENT_STEP,
        };

        try {
            const isUpdate = !!stepsData?.id;
            let response;

            if (isUpdate) {
                response = await callExternalDataApi(
                    `${API_CUSTOM_EXTERNAL_DATA_CONNECTION}/${stepsData.id}`,
                    'PUT',
                    payload
                );
            } else {
                response = await callExternalDataApi(
                    API_CUSTOM_EXTERNAL_DATA_CONNECTION,
                    'POST',
                    payload
                );
            }

            const isSuccess = response?.data?.code === 200 || response?.data?.code === 201;
            const responseData = response?.data?.data;

            if (isSuccess && responseData) {
                const existsIdx = connectedAccounts.findIndex(
                    acc => acc.id === (isUpdate ? stepsData.id : responseData.id)
                );

                let nextAccounts;
                if (existsIdx !== -1) {
                    nextAccounts = [...connectedAccounts];
                    nextAccounts[existsIdx] = {
                        ...connectedAccounts[existsIdx],
                        ...payload,
                        ...(responseData || {}),
                    };
                } else {
                    nextAccounts = [{ ...payload, ...(responseData || {}), jobStatus: '' }, ...connectedAccounts];
                }

                setStepsData({
                    ...stepsData,
                    ...payload,
                    ...(responseData || {}),
                });

                dispatch(setCustomExternalDataConnectedAccounts(nextAccounts));
                toast.success(
                    `External Data Set ${isUpdate ? 'updated' : 'created'} successfully.`
                );
                setStepSubmitSnapshot(prev => ({
                    ...prev,
                    [CURRENT_STEP]: { ...values },
                }));
                setActiveStep(CURRENT_STEP + 1);
                setRefetching({
                    objectSettings: {
                        refetching: true,
                    }   
                });
            } else {
                const errMsg =
                    response?.data?.message ||
                    'API Error: Something went wrong. Please try again later.';

                toast.error(errMsg);
            }
        } catch (err) {
            console.error('Submit error:', err);
            toast.error('Something went wrong. Please try again later.');
        } finally {
            setSubmitting(false);
            onFormDirty(CURRENT_STEP, false);
        }
    }, [setActiveStep, setStepsData, stepsData]); // eslint-disable-line react-hooks/exhaustive-deps

    const validationSchema = Yup.object().shape({
        name: Yup.string()
            .trim()
            .required('Name is required')
            .max(200, 'Name cannot be more than 200 characters')
            .test(
                'unique',
                'Name already exists',
                function (value) {
                    if (!value) return true;
                    const lowerValue = value.trim().toLowerCase();

                    return !connectedAccounts.some(
                        acc =>
                            acc.name?.trim().toLowerCase() === lowerValue &&
                            acc.id !== stepsData.id
                        );
                }
            ),
    });

    return (
        <StepCardHeader
            title={`Name & Description`}
            step={CURRENT_STEP}
            activeStep={activeStep}
            lastEditStep={stepsData.lastEditStep}
            setActiveStep={() => setActiveStep(CURRENT_STEP)}
        >
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
                validateOnBlur={true}
                enableReinitialize
            >
                {({ values, errors, touched, handleChange, isSubmitting, handleSubmit }) => {
                    return (
                        <CForm onSubmit={handleSubmit} noValidate>
                            <SyncFormikValues
                                values={values}
                                setStepsData={setStepsData}
                                step={CURRENT_STEP}
                            />
                            <FormikDirtyWatcher
                                step={CURRENT_STEP}
                                values={values}
                                snapshot={stepSubmitSnapshot[CURRENT_STEP]}
                                defaultInitialValues={defaultInitialValuesStep}
                                onDirty={onFormDirty}
                            />
                            <CRow>
                                <CCol lg="12">
                                    <FormGroup
                                        name="name"
                                        label="Name this External Data Set to identify the data being imported"
                                        values={values}
                                        errors={errors}
                                        touched={touched}
                                        placeholder="Enter a name"
                                        onChange={handleChange}
                                    />
                                </CCol>

                                <CCol lg="12">
                                    <FormGroup
                                        name="description"
                                        label="Describe the connection for your own internal use"
                                        values={values}
                                        errors={errors}
                                        touched={touched}
                                        placeholder="Enter a description"
                                        onChange={handleChange}
                                    />
                                </CCol>
                            </CRow>

                            <CButton disabled={isSubmitting || canUpdate} type="submit" className="px-4" color="primary">
                                <Waiting isLoading={isSubmitting}>Save & Next</Waiting>
                            </CButton>
                        </CForm>
                    );
                }}
            </Formik>
        </StepCardHeader>
    );
};
export default NameAndDescription;